// Passing Report (?live=1 only, Ben 2026-10-20) — Catalyst's Passing page, made explorable. Step 1:
// everything from the player's own passes (passing-report-view.ts, by Catalyst's engine rules):
// - Summary: passes, success % vs the xPass model, PSAX, average difficulty;
// - Style: the attempt mix (hard / medium / easy), the direction mix and the final-third direction mix;
// - Difficulty bands: tried, completed, expected, success %, PSAX per band;
// - Tendencies: pass length, long balls, average start, into pressure / danger, left foot, final third.
// Every bar segment, band and set-like tendency is a set of passes: a click highlights them on any open
// Pass Map (catalyst:event-selection, as a Passing Profile wedge does; again → off), a drag drops them
// onto a Pass Map / Passing Profile / Heat Map as a filter chip ("Only: Hard passes", ✕ to clear).
// Step 2 (later): the position average beside each number.
//
// Instance state: `_passingPick` (the clicked set), plus the event kit's competition / season and a
// match / position dragged in from Matches (`_eventMatchFocus`).

import { useEffect, useMemo, useReducer, useState, type PointerEvent as ReactPointerEvent } from "react";
import type { ProtoEvent } from "@catbento/shared";
import { type ProtoModuleProps, getModState, setModState } from "./bridge.js";
import { liveShortName, useRefresh } from "./EventMapBody.js";
import { EventSubControls } from "./EventSubControls.js";
import { EventFilterBar } from "./EventFilterBar.js";
import { matchesEventFilters, passFilters, pruneStaleEventFilters, type EventActive } from "../engines/event-map-kit.js";
import { FocusChip } from "./FocusChip.js";
import { ModuleExplainer } from "./ModuleExplainer.js";
import { eventChipsOf, explainPassingReport } from "./explain.js";
import { humanizeSeasonCode } from "./league-table-view.js";
import { noDataMessage } from "./no-data.js";
import {
  PASS_SET_LABEL,
  actualVsExpected,
  inPassSet,
  passingReport,
  type PassSetKey,
  type ShareSegment,
} from "./passing-report-view.js";
import { focusEmptyText, inFocus, moduleFocus, type EventFocus } from "../live/event-focus.js";
import { useLiveEventMap } from "../hooks/useLiveEventMap.js";
import { EVENT_KINDS } from "../live/event-map.js";
import { LIVE } from "../live/flag.js";
import { releasePasses, selectPasses } from "../engines/event-link.js";
import { useLinkedSelection } from "./useLinkedSelection.js";
import { LinkedChip } from "./LinkedChip.js";
import { startMatchDrag } from "../canvas/match-drag.js";
import { useLiveQuery } from "../live/query.js";
import { liveApi } from "../live/api.js";
import { loadGroupFile } from "../live/group-players.js";
import { passingAverages, type PassingAverages } from "../live/passing-average.js";
import { undoApi } from "../canvas/undo.js";

const EMPTY: ProtoEvent[] = [];

export function PassingReportModule(props: ProtoModuleProps) {
  // Real data only: the snapshot site has no tile for it (legacy-scaffold.html `data-live-only`).
  if (!LIVE) return <div className="stats-empty">The Passing Report works on real data only.</div>;
  return <LivePassingReport {...props} />;
}

function LivePassingReport(props: ProtoModuleProps) {
  const { mod, body, playerId } = props;
  const refresh = useRefresh();
  const [, rerender] = useReducer((x: number) => x + 1, 0);
  const { spd, state, ctx } = useLiveEventMap(mod, playerId, EVENT_KINDS.passes);
  // The filter chips (Ben, 2026-10-21), the same row as the Pass Map and Passing Profile.
  const [filterVersion, setFilterVersion] = useState(0);
  const defs = useMemo(() => passFilters(ctx), [ctx]);
  const shortName = liveShortName(props);

  useEffect(() => {
    body.style.justifyContent = "flex-start";
  }, [body]);

  const seasonPasses = state ? state.season.passes : EMPTY;
  const focus = moduleFocus(mod, playerId);
  const scope = useMemo(() => (focus ? seasonPasses.filter((e) => inFocus(e, focus)) : seasonPasses), [seasonPasses, focus]);
  // The shared selection (Ben, 2026-10-21): the other modules' picks narrow every number here to their
  // passes (a blue chip each); they add to this report's own pick (2026-10-26), which goes only when
  // it's ✕'d elsewhere.
  const linked = useLinkedSelection(mod, scope, () => {
    if (getModState<PassSetKey>(mod, "_passingPick")) {
      setModState(mod, "_passingPick", undefined);
      rerender();
    }
  });
  // Seeded once there's data, as the maps do; self-healed against the season's passes every render.
  if (state && !getModState<EventActive>(mod, "_eventFilters")) setModState(mod, "_eventFilters", {});
  const active = getModState<EventActive>(mod, "_eventFilters") ?? null;
  if (state && active) pruneStaleEventFilters(active, scope, defs);
  const chipped = useMemo(
    () => (active ? scope.filter((e) => matchesEventFilters(e, active, defs)) : scope),
    // filterVersion stands in for the in-place edits to `active`.
    [scope, active, defs, filterVersion],
  );
  const passes = useMemo(() => (linked ? chipped.filter((e) => linked.ids.has(e.id)) : chipped), [chipped, linked]);
  const report = useMemo(() => passingReport(passes), [passes]);

  // Step 2 (Ben, 2026-10-20): the position average beside each number, from this league season's
  // Stats file (live/passing-average.ts). None when the season's file isn't built or they're not in it.
  const compId = state ? Number(state.compId) : null;
  const seasonId = state ? Number(state.seasonCode) : null;
  const group = useLiveQuery(compId === null ? null : `group:file:${compId}:${seasonId}`, () =>
    loadGroupFile(liveApi(), compId as number, seasonId as number),
  );
  const avg: PassingAverages | null = useMemo(
    () => (group && state && playerId !== null ? passingAverages(group, playerId, state.comp.name) : null),
    [group, state, playerId],
  );
  /** "avg 84.1%" for a file column, or null with no average. */
  const avgText = (key: string | undefined, type: "percentage" | "decimal" = "percentage"): string | null => {
    const v = key && avg ? (avg.values as Record<string, number | null | undefined>)[key] : null;
    if (v === null || v === undefined) return null;
    return type === "percentage" ? `${(v * 100).toFixed(1)}%` : Math.abs(v) >= 10 ? v.toFixed(1) : v.toFixed(2);
  };
  /** The average as a tooltip line (Ben, 2026-10-20: on hover only, so the report stays clean):
   *  "\nPremier League midfielders: 35.0%", or "" with none. */
  const avgLine = (key: string | undefined, type: "percentage" | "decimal" = "percentage", suffix = ""): string => {
    const t = avgText(key, type);
    return t && avg ? `\n${avg.label}: ${t}${suffix}` : "";
  };

  const picked = getModState<PassSetKey>(mod, "_passingPick") ?? null;
  const idsOf = (key: PassSetKey) => passes.filter((e) => inPassSet(key, e)).map((e) => e.id);
  // This report's pick for the shared selection (engines/event-link.ts): its picked set and / or its
  // filter chips. Announced when the user changes either, never on a redraw.
  const announce = () => {
    if (!active) return;
    const set = getModState<PassSetKey>(mod, "_passingPick") ?? null;
    const chips = eventChipsOf(active, defs).map((c) => `${c.label}: ${c.value}`);
    if (set === null && chips.length === 0) {
      releasePasses(mod);
      return;
    }
    // Its own passes only: the others' picks are layered on by whoever shows them.
    const ids = scope.filter((e) => matchesEventFilters(e, active, defs) && (set === null || inPassSet(set, e))).map((e) => e.id);
    selectPasses({ ids, label: [set ? PASS_SET_LABEL[set] : null, ...chips].filter(Boolean).join(", "), source: mod });
  };
  const pick = (key: PassSetKey) => {
    undoApi().saveState();
    const next = picked === key ? null : key;
    setModState(mod, "_passingPick", next ?? undefined);
    // The Pass Map and Passing Profile follow it.
    announce();
    rerender();
  };
  // A drag takes the set onto a map as a filter; a plain click picks it (the drag starts only once
  // the pointer moves, so the click still lands).
  const dragSet = (e: ReactPointerEvent<HTMLElement>, key: PassSetKey) => {
    if (!state || playerId === null || e.button !== 0) return;
    const f: EventFocus = {
      kind: "passes",
      playerId,
      competitionId: Number(state.compId),
      seasonCode: state.seasonCode,
      label: focus ? `${PASS_SET_LABEL[key]} · ${focus.label}` : PASS_SET_LABEL[key],
      matches: [],
      ids: idsOf(key),
    };
    startMatchDrag(e.nativeEvent, mod, f);
  };
  const setProps = (key: PassSetKey, count: number, what: string) => ({
    "data-tooltip": `${what}\nClick to pick ${count === 1 ? "it" : "them"} on the Pass Map and Passing Profile too · drag onto a map to filter it`,
    onClick: () => pick(key),
    onPointerDown: (e: ReactPointerEvent<HTMLElement>) => dragSet(e, key),
  });

  const subControls = (
    <EventSubControls
      mod={mod}
      spd={spd}
      state={state}
      onCompetitionChange={(compId) => {
        setModState(mod, "_eventMapCompetitionId", compId);
        Reflect.deleteProperty(mod, "_eventMapSeason");
        refresh();
      }}
      onSeasonChange={(seasonCode) => {
        setModState(mod, "_eventMapSeason", seasonCode);
        refresh();
      }}
    />
  );

  if (!spd || !state) {
    return (
      <>
        {subControls}
        <div className="stats-empty">{noDataMessage("pass", shortName, state?.seasonCode ?? null)}</div>
      </>
    );
  }
  if (!passes.length) {
    return (
      <>
        {subControls}
        {focus && seasonPasses.length ? (
          <>
            <FocusChip mod={mod} focus={focus} />
            <div className="stats-empty">{focusEmptyText(shortName, "passes", focus)}</div>
          </>
        ) : (
          <div className="stats-empty">{noDataMessage("pass", shortName, state.seasonCode)}</div>
        )}
      </>
    );
  }

  const ave = actualVsExpected(report);
  const bar = (title: string, segments: ShareSegment[], note: string) => {
    const total = segments.reduce((t, s) => t + s.count, 0);
    return (
      <div className="pr-bar-block">
        <div className="pr-bar-head">
          <span>{title}</span>
          <span className="pr-muted">{note}</span>
        </div>
        {total ? (
          <>
            <div className="pr-bar">
              {segments.map((s) =>
                s.count ? (
                  <span
                    key={s.key}
                    {...setProps(s.key, s.count, `${PASS_SET_LABEL[s.key]}: ${s.count.toLocaleString()} (${(s.share * 100).toFixed(1)}%)${avgLine(s.avgKey)}`)}
                    className={`pr-seg pr-seg-${s.key}${picked === s.key ? " picked" : ""}`}
                    style={{ flexGrow: s.count }}
                  />
                ) : null,
              )}
            </div>
            <div className="pr-bar-legend">
              {segments.map((s) => (
                <span
                  key={s.key}
                  className={`pr-legend-item${picked === s.key ? " picked" : ""}`}
                  {...setProps(s.key, s.count, `${PASS_SET_LABEL[s.key]}: ${(s.share * 100).toFixed(1)}%${avgLine(s.avgKey)}`)}
                >
                  <span className={`pr-dot pr-seg-${s.key}`} />
                  {s.label} <b>{(s.share * 100).toFixed(0)}%</b>
                </span>
              ))}
            </div>
          </>
        ) : (
          <div className="pr-muted">None in this selection.</div>
        )}
      </div>
    );
  };

  return (
    <>
      <ModuleExplainer
        mod={mod}
        paragraphs={explainPassingReport({
          name: shortName,
          competition: state.comp.name,
          season: humanizeSeasonCode(state.seasonCode),
          passes: report.passes,
          focus,
          picked: picked ? PASS_SET_LABEL[picked] : null,
        })}
      />
      {subControls}
      <div className="pr-wrap">
        {active && (
          <EventFilterBar
            active={active}
            events={scope}
            defs={defs}
            onChange={() => {
              setFilterVersion((v) => v + 1);
              announce();
            }}
            extra={
              <>
                {focus && <FocusChip mod={mod} focus={focus} inline />}
                {linked && <LinkedChip linked={linked} />}
              </>
            }
          />
        )}

        <div className="pr-summary">
          <div className="pr-card" data-tooltip="Passes attempted (offside passes aren't counted).">
            <div className="pr-card-value">{report.passes.toLocaleString()}</div>
            <div className="pr-card-label">Passes</div>
          </div>
          <div
            className="pr-card"
            data-tooltip={`Pass success against what the xPass model expected from the same passes.${avgLine("pass_success_perc", "percentage", avgText("expected_pass_success_perc") ? ` vs ${avgText("expected_pass_success_perc")}` : "")}`}
          >
            {/* Two lines (Ben, 2026-10-21): the success % with how far above / below expected beside
                it, then what it's measured against. */}
            <div className="pr-card-value">
              {report.successPct === null ? "—" : `${(report.successPct * 100).toFixed(1)}%`}
              {report.successPct !== null && (
                <span className={`pr-card-delta ${report.psax >= 0 ? "pr-up" : "pr-down"}`}>{ave.sub.replace(" vs expected", "")}</span>
              )}
            </div>
            <div className="pr-card-label">
              {report.expectedPct === null ? "—" : `${(report.expectedPct * 100).toFixed(1)}%`} Exp. Pass Succ.
            </div>

          </div>
          <div className="pr-card" data-tooltip={`Passes completed above (or below) expected, summed over every pass.${avgLine("psax_perc", "percentage", " per pass")}`}>
            {/* Two lines, as the success card (Ben, 2026-10-21). */}
            <div className="pr-card-value">
              <span className={report.psax >= 0 ? "pr-up" : "pr-down"}>
                {report.psax >= 0 ? "+" : "−"}
                {Math.abs(report.psax).toFixed(2)}
              </span>
            </div>
            <div className="pr-card-label">PSAX{report.passes > 0 ? ` · ${((report.psax / report.passes) * 100).toFixed(1)}% per pass` : ""}</div>

          </div>
          <div
            className="pr-card"
            data-tooltip={`How hard the passes were on average: 1 minus the chance each would succeed. Higher = harder passes tried.${avgLine("avg_pass_difficulty", "decimal")}`}
          >
            <div className="pr-card-value">{report.avgDifficulty === null ? "—" : report.avgDifficulty.toFixed(2)}</div>
            <div className="pr-card-label">Avg difficulty</div>

          </div>
        </div>

        <div className="pr-section-title">Style</div>
        {bar("Attempt mix", report.attemptMix, "hard < 50% likely · easy > 80%")}
        {bar("Direction", report.directionMix, "all passes")}
        {bar("Final third direction", report.finalThirdMix, "completed, open play")}

        <div className="pr-section-title">Difficulty bands</div>
        <table className="pr-bands">
          <thead>
            <tr>
              <th>Band</th>
              <th>Tried</th>
              <th>Completed</th>
              <th>Expected</th>
              <th>Success</th>
              <th>Expected</th>
              <th>PSAX</th>
            </tr>
          </thead>
          <tbody>
            {report.bands.map((b) => (
              <tr
                key={b.key}
                className={`pickable${picked === b.key ? " picked" : ""}`}
                {...setProps(b.key, b.attempted, `${PASS_SET_LABEL[b.key]}: ${b.attempted.toLocaleString()}${avgLine(b.avgKey, "percentage", " success")}`)}
              >
                <td>
                  <span className={`pr-dot pr-seg-${b.key}`} />
                  {b.label}
                </td>
                <td>{b.attempted.toLocaleString()}</td>
                <td>{b.completed.toLocaleString()}</td>
                <td>{b.expected.toFixed(1)}</td>
                <td>
                  {b.successPct === null ? "—" : `${(b.successPct * 100).toFixed(1)}%`}
                </td>
                <td className="pr-muted">{b.expectedPct === null ? "—" : `${(b.expectedPct * 100).toFixed(1)}%`}</td>
                <td className={b.psax >= 0 ? "pr-up" : "pr-down"}>
                  {b.psax >= 0 ? "+" : "−"}
                  {Math.abs(b.psax).toFixed(1)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="pr-section-title">Tendencies</div>
        {/* The sets (dashed cards) first; the two averages after them as plain numbers, so nothing
            looks clickable that isn't (Ben, 2026-10-20). */}
        <div className="pr-tendencies">
          {[...report.tendencies].sort((a, b) => Number(!a.key) - Number(!b.key)).map((t) =>
            t.key ? (
              <div key={t.label} {...setProps(t.key, 0, `${t.label}: ${t.value}${avgLine(t.avgKey, t.avgType)}\n${t.definition}`)} className={`pr-tendency pickable${picked === t.key ? " picked" : ""}`}>
                <div className="pr-tendency-value">{t.value}</div>
                <div className="pr-tendency-label">{t.label}</div>
              </div>
            ) : (
              <div key={t.label} className="pr-tendency pr-tendency-plain" data-tooltip={`${t.label}: ${t.value}${avgLine(t.avgKey, t.avgType)}\n${t.definition}`}>
                <div className="pr-tendency-value">{t.value}</div>
                <div className="pr-tendency-label">{t.label}</div>
              </div>
            ),
          )}
        </div>
      </div>
    </>
  );
}
